import { useState, useEffect } from 'react';
import { supabase } from '../../shared/services/supabase';

export interface FeaturedCreator {
  id: string;
  username: string | null;
  profile_picture: {
    url: string;
    id?: string;
  } | null;
}

export function useFeaturedCreators() {
  const [creators, setCreators] = useState<FeaturedCreator[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchCreators() {
      try {
        setLoading(true);
        setError(null);

        // Simplified query - no filters, no ordering, just get data and filter client-side
        // This avoids RLS complexity that might cause timeouts
        const { data, error: fetchError } = await supabase
          .from('v_profile_details')
          .select('id, username, profile_picture')
          .limit(20); // Get more than needed, filter client-side

        if (fetchError) {
          // Handle timeout or other errors gracefully
          if (fetchError.code === '57014' || fetchError.message?.includes('timeout')) {
            console.warn('Query timeout - using fallback creators');
          } else {
            console.warn('Could not fetch featured creators:', fetchError);
          }
          setCreators([]);
          return;
        }

        // Transform and filter client-side - this is fast
        const transformed: FeaturedCreator[] = [];
        
        for (const profile of (data || [])) {
          // Skip if missing required fields
          if (!profile.username || !profile.profile_picture) {
            continue;
          }

          let profilePicture = null;
          
          // Handle profile_picture - it might be a string (JSON) or already an object
          if (profile.profile_picture) {
            try {
              if (typeof profile.profile_picture === 'string') {
                profilePicture = JSON.parse(profile.profile_picture);
              } else {
                profilePicture = profile.profile_picture;
              }
            } catch (e) {
              // If parsing fails, skip this creator
              continue;
            }
          }

          // Only include if we have a valid URL
          if (!profilePicture || !profilePicture.url) {
            continue;
          }

          transformed.push({
            id: profile.id,
            username: profile.username,
            profile_picture: {
              url: profilePicture.url,
              id: profilePicture.media_id || profilePicture.id,
            },
          });

          // Stop when we have 5 valid creators
          if (transformed.length >= 5) {
            break;
          }
        }

        setCreators(transformed);
      } catch (e) {
        console.error('Error fetching featured creators:', e);
        setError(e instanceof Error ? e.message : 'Failed to load creators');
        setCreators([]);
      } finally {
        setLoading(false);
      }
    }

    fetchCreators();
  }, []);

  return { creators, loading, error };
}
